import {
  useEffect,
  useState,
  createContext,
  FunctionComponent,
  ReactElement,
  useMemo,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Stack, Text, useToast } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { MdContentCopy } from "react-icons/md";

import Api from "src/Config";

import { textTruncate } from "src/helpers/truncateText";

import { useCopyToClipboard } from "src/hooks/useCopyToClipboard";
import { Switch } from "src/components";

export const AMLConfigurationContext = createContext(null);

type Props = {
  children: ReactElement;
};

const AMLConfigurationProvider: FunctionComponent<Props> = ({ children }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useSearchParams();
  const pageQuery = queryParams.get("page") || 1;

  const [searchParam, setSearchParam] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const [, copy] = useCopyToClipboard();

  const handleCopy = (text: string) => () => {
    copy(text)
      .then(() => {
        toast({
          description: `Configuration copied to clipboard successfully`,
          status: "success",
          duration: 9000,
          isClosable: true,
          variant: "left-accent",
        });
      })
      .catch((error) => {
        toast({
          description: error,
          status: "warning",
          duration: 9000,
          isClosable: true,
          variant: "left-accent",
        });
      });
  };

  // const debouncedValue = useDebounce<string>(searchParam, 800);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const handleUpdateRuleAction = async (e: any, id: any) => {
    const payload = { id, action: e.target.checked };
    try {
      const response = await linkGenerationUpdateMutateAsync(payload);

      if (response.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["aml_configuration"] });
        toast({
          description: response.data.detail || "Rule updated successfully",
          status: "success",
          duration: 9000,
          isClosable: true,
          variant: "left-accent",
        });
      }
    } catch (error: any) {
      const err: string = error?.response?.data.detail
        ? error?.response?.data?.detail
        : "An error occurred, Please try again";
      toast({
        description: err,
        status: "error",
        duration: 9000,
        isClosable: true,
        variant: "left-accent",
      });
    }
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: [
      "aml_configuration",
      searchValue,
      // debouncedValue,
      pageQuery,
      queryParams.toString(),
    ],
    staleTime: 30000,
    enabled: queryParams.get("tab") === "aml-configuration",
    // enabled: tabQuery === "single",
    queryFn: async () => {
      const url = searchValue
        ? `settings/onboarding/external-verification-links?search=${searchValue}&page_size=10&page=1`
        : `settings/onboarding/external-verification-links?page_size=10&${decodeURIComponent(
            queryParams as any,
          )}`;
      const { data } = await Api.get(`${url}`);

      const response = data.results.map(
        (res: {
          id: string;
          name: string;
          description: string;
          check_types: any;
          more_checks: any;
          intelligence_checks: any;
          kyc_endpoints: any;
          is_active: boolean;
          created_at: string;
        }) => ({
          id: res.id,
          "on/off": (
            <Stack direction={"row"}>
              <Switch
                size="sm"
                color="#251169"
                handleChange={(e: any) => handleUpdateRuleAction(e, res.id)}
                defaultChecked={res.is_active}
              />
            </Stack>
          ),
          name: `${res.name}`,
          description: res.description,
          check_types: res.check_types,
          "configuration code": (
            <Stack
              direction={"row"}
              alignItems={"center"}
              cursor={"pointer"}
              onClick={handleCopy(
                `${process.env.REACT_APP_RADAR_AML_GENERATION_CONFIG_CLIENT}?configId=${res.id}`,
              )}
              position={"relative"}
            >
              <Text>{textTruncate(res.id, 15)}</Text>
              <MdContentCopy />
            </Stack>
          ),
          "check types": (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {res.check_types?.join(", ")}
            </div>
          ),
          "more checks": (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {res.more_checks?.join(", ")?.replace(/_/, " ") || "-"}
            </div>
          ),
          more_checks: res.more_checks,
          intelligence_checks: res.intelligence_checks,
          kyc_endpoint: res.kyc_endpoints,
          date: format(new Date(res.created_at), "dd/MM/yyyy"),
        }),
      );
      return { response, metadata: data?.metadata };
    },
  });

  const {
    data: kycEndpointData,
    isLoading: kycEndpointIsLoading,
    isFetching: kycEndpointIsFetching,
  } = useQuery({
    queryKey: ["kyc_endpoint"],
    queryFn: async () => {
      const {
        data: { data },
      } = await Api.get(`onboarding/kyc/endpoints`);

      return data;
    },
  });

  useEffect(() => {
    if (data) {
      // setResponse(data?.results);
      setPageCount(Math.ceil(data?.metadata?.count / 10));
      // setPageCount(10);
    }
  }, [data]);

  const handleSearch = (e: any) => {
    if (e.target.value) {
      setSearchParam(e.target.value);
    } else {
      setSearchValue("");
      setSearchParam("");
    }
  };

  const {
    mutateAsync,
    data: linkGenerationData,
    isPending,
    error: linkGenerationError,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.post(
        `settings/onboarding/external-verification-links`,
        payload,
      );
      return data;
    },
    onError: (err: any) => {
      throw err;
    },
  });

  const {
    mutateAsync: linkGenerationMutateAsync,
    data: linkGenerationDeleteData,
    isPending: linkGenerationIsPending,
    error: linkGenerationDeleteError,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.delete(
        `settings/onboarding/external-verification-links/${payload.id}`,
      );
      return data;
    },
    onError: (err) => {
      throw err;
    },
  });

  const {
    mutateAsync: linkGenerationUpdateMutateAsync,
    // data: ruleData,
    isPending: linkGenerationUpdateIsPending,
    // error: errorRule,
  } = useMutation({
    mutationFn: async (payload: any) => {
      const data = await Api.put(
        `settings/onboarding/external-verification-links/${payload.id}`,
        payload,
      );
      return data;
    },
    onError: (err) => {
      console.log(err, "###");
    },
  });

  const handleSearchRecord = () => {
    setSearchValue(searchParam);
  };

  const contextValue = useMemo(
    () => ({
      data,
      isLoading,
      handleSearch,
      searchParam,
      isFetching,
      // handlePaginate,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
      handleSearchRecord,
      kycEndpointData,
      kycEndpointIsLoading,
      kycEndpointIsFetching,
      mutateAsync,
      linkGenerationData,
      linkGenerationError,
      isPending,
      linkGenerationMutateAsync,
      linkGenerationDeleteData,
      linkGenerationIsPending,
      linkGenerationDeleteError,
      linkGenerationUpdateMutateAsync,
      linkGenerationUpdateIsPending,
    }),
    [
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      // handlePaginate,
      pageNumber,
      pageCount,
      setPageNumber,
      pageQuery,
      setQueryParams,
      handleSearchRecord,
      kycEndpointData,
      kycEndpointIsLoading,
      kycEndpointIsFetching,
      mutateAsync,
      linkGenerationData,
      linkGenerationError,
      isPending,
      linkGenerationMutateAsync,
      linkGenerationDeleteData,
      linkGenerationIsPending,
      linkGenerationDeleteError,
      linkGenerationUpdateMutateAsync,
      linkGenerationUpdateIsPending,
    ],
  );
  return (
    <AMLConfigurationContext.Provider value={contextValue}>
      {children}
    </AMLConfigurationContext.Provider>
  );
};

export default AMLConfigurationProvider;
